import { Text } from '@mantine/core';
import dayjs from 'dayjs';
import { i18nKeys, type TranslateFunction } from 'locales';
import { TasksListItemStatus } from 'modules/Tasks';
import type { ReactNode } from 'react';
import { NumericFormat } from 'react-number-format';
import { isNullish } from 'remeda';
import Amount from 'shared/components/Amount';
import StatusItem from 'shared/components/StatusItem';
import { formatCommunication } from 'shared/utils/mask.utils';
import type { TableColumn } from 'types/views';

type CurrencyFormatter = { format: (value: number) => string };

export type CellContext = {
  t: TranslateFunction;
  formatter: CurrencyFormatter;
  actionsCol?: (item: any) => ReactNode;
};

export type CellRenderer = (item: any, column: TableColumn, ctx: CellContext) => ReactNode;

// Columns whose value is rendered through <Amount> (localized money), keyed by name.
const MONEY_COLUMNS = [
  'total_ca',
  'annual_ca',
  'total_tvac',
  'total_htva',
  'late_fees',
  'ignored_late_fees',
  'remaining_late_fees',
  'remaining_balance',
  'remaining_balance_with_fees',
  'remaining_balance_without_late_fees',
  'total_discount_amount',
  'overdue_amount',
  'payment_plan_remaining_balance',
  'interest_amount',
  'credit_notes_total',
  'payments_sum',
  'penalty_clause_amount',
];

const buildColumnParser = (t: TranslateFunction, formatter: CurrencyFormatter) => ({
  date: (value: any) => dayjs(value).format('L'),
  boolean: (value: any) => (value ? t(i18nKeys.YES) : t(i18nKeys.NO)),
  money: (value: any) => formatter.format(value).replace(/,(\d{1,2})(?=\s*[€$£¥]?\s*$)/, '.$1'),
  money_in_cents: (value: any) => formatter.format(Number(value) / 100),
  // eslint-disable-next-line react/no-unstable-nested-components
  percentage: (value: any) => (
    <NumericFormat value={value} suffix="%" decimalScale={2} displayType="text" />
  ),
  days: (value: any) => t(i18nKeys.DAY_S, { count: value }),
  invoice_count: (value: number) => t(i18nKeys.INVOICE_COUNT, { count: value }),
  structured_communication: (value: any) => formatCommunication(value),
});

type ColumnParser = ReturnType<typeof buildColumnParser>;
const parserCache = new WeakMap<TranslateFunction, WeakMap<CurrencyFormatter, ColumnParser>>();
const getColumnParser = (t: TranslateFunction, formatter: CurrencyFormatter): ColumnParser => {
  let byFormatter = parserCache.get(t);
  if (!byFormatter) {
    byFormatter = new WeakMap();
    parserCache.set(t, byFormatter);
  }
  let parser = byFormatter.get(formatter);
  if (!parser) {
    parser = buildColumnParser(t, formatter);
    byFormatter.set(formatter, parser);
  }
  return parser;
};

// Resolves a cell's value: debtor custom variable, then custom variable, then the raw
// field; formatted through the column's parser unless it is exempt or null.
const parseValue = (item: any, column: TableColumn, ctx: CellContext): ReactNode => {
  const value =
    item.debtor_custom_variables?.[column.name.replace('debtor__', '')] ||
    item.custom_variables?.[column.name] ||
    item[column.name];

  const columnParser = getColumnParser(ctx.t, ctx.formatter);

  if (
    columnParser[column.format as keyof typeof columnParser] &&
    column.name !== 'next_step_date' &&
    !isNullish(value)
  ) {
    return columnParser[column.format as keyof typeof columnParser](value);
  }

  return value;
};

// Status renderers keyed by the column's cleaned_view. Views without an entry fall
// back to the invoice/debtor StatusItem — a new view-type registers here instead of
// adding a branch to the cell. (Item type reused from CellRenderer, which is loose.)
type StatusRenderer = (item: Parameters<CellRenderer>[0]) => ReactNode;

const statusByView: Record<string, StatusRenderer> = {
  tasks: item => <TasksListItemStatus status={item.status} statusKey={item.status_key} />,
};

const defaultStatus: StatusRenderer = item => (
  <StatusItem status={item.status} step={item.step_collection} />
);

const statusContent: CellRenderer = (item, column) => {
  const { cleaned_view } = column as { cleaned_view?: string };
  const renderStatus = (cleaned_view && statusByView[cleaned_view]) || defaultStatus;
  return renderStatus(item);
};

const moneyContent: CellRenderer = (item, column) => {
  const value = item?.localized_money_object?.[column.name] || item?.[column.name];
  if (value == null) return '-';

  return <Amount localizedValue={item?.[column.name]} value={value} suffix={item.currency} />;
};

const actionsContent: CellRenderer = (item, _column, { actionsCol }) => actionsCol?.(item);

const defaultContent: CellRenderer = (item, column, ctx) => {
  const parsed = parseValue(item, column, ctx);
  if (parsed === ctx.t(i18nKeys.INVOICING.IMPENDING_REMINDERS.WAITING_VALIDATION)) {
    return (
      <Text c="red.5" fz="xs" span>
        {parsed}
      </Text>
    );
  }
  // Dash for empty/missing values, but keep a legitimate 0 (don't let `|| '-'` eat it).
  return parsed == null || parsed === '' || parsed === false ? '-' : parsed;
};

export const resolveCellContent = (column: TableColumn): CellRenderer => {
  if (column.name === 'status') return statusContent;
  if (MONEY_COLUMNS.includes(column.name)) return moneyContent;
  if (column.column_type === 'actions') return actionsContent;
  return defaultContent;
};
